import { startTransition, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { DatePicker } from '@mantine/dates'
import { Group, MantineStyleProps, Stack, UnstyledButton } from '@mantine/core'
import { useTranslation } from 'react-i18next'

type RangePreset = {
  value: [string, string]
  label: string
}

const formatYmd = (date: Date | null): string | null => (date ? dayjs(date).format('YYYY-MM-DD') : null)

// Parse a 'YYYY-MM-DD' string as a local-midnight Date so the value we hand back
// to callers does not silently shift across the UTC boundary.
const parseYmd = (value: string): Date => dayjs(value, 'YYYY-MM-DD').toDate()

export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeChange,
  onRangeSelectionStateChange,
  mb = 'md',
  presets,
}: {
  startDate: Date | null
  endDate: Date | null
  onStartDateChange?: (date: Date | null) => void
  onEndDateChange?: (date: Date | null) => void
  onRangeChange?: (startDate: Date | null, endDate: Date | null) => void
  onRangeSelectionStateChange?: (isRangeSelectionInProgress: boolean) => void
  mb?: MantineStyleProps['mb']
  presets?: RangePreset[]
}) {
  const { t } = useTranslation('common')
  const resolvedPresets: RangePreset[] = presets ?? [
    {
      value: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('today'),
    },
    {
      value: [dayjs().subtract(2, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastTwoDays'),
    },
    {
      value: [dayjs().subtract(7, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastSevenDays'),
    },
    {
      value: [dayjs().subtract(14, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastFourteenDays'),
    },
    {
      value: [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastThirtyDays'),
    },
  ]

  // Stabilise the prop dates by their YMD string form. The parent typically
  // recreates `Date` objects on every render (e.g. via `parseAndValidateDates`),
  // so depending on the raw Date references would make the sync effect fire on
  // every parent re-render — overwriting in-progress range selection.
  const startKey = formatYmd(startDate)
  const endKey = formatYmd(endDate)

  // Controlled value held internally so partial range selection (first click only)
  // can render in the calendar without the parent having to model the in-progress state.
  const [internalValue, setInternalValue] = useState<[string | null, string | null]>([startKey, endKey])
  const [displayedDate, setDisplayedDate] = useState<string>(endKey ?? dayjs().format('YYYY-MM-DD'))

  // Sync internal value only when the parent's startDate/endDate actually change
  // (e.g. URL params updating after navigation), not on every re-render.
  // Also bring the calendar to the new range when it was changed from outside
  // (e.g. the header arrows) and neither endpoint is in the displayed month.
  useEffect(() => {
    setInternalValue([startKey, endKey])
    setDisplayedDate(current =>
      [startKey, endKey].some(key => key && dayjs(key).isSame(current, 'month')) ? current : (endKey ?? current)
    )
  }, [startKey, endKey])

  const propagate = (value: [string | null, string | null]) => {
    const [s, e] = value
    const isRangeSelectionInProgress = Boolean((s && !e) || (!s && e))
    onRangeSelectionStateChange?.(isRangeSelectionInProgress)

    // Wrap the parent callbacks (which typically trigger URL navigation and a
    // suspending query) in a transition so React keeps the previous page UI
    // visible while the new data loads, instead of swapping to the Suspense
    // fallback and shifting layout on every arrow press / preset click.
    startTransition(() => {
      if (s && e) {
        const d1 = parseYmd(s)
        const d2 = parseYmd(e)
        if (onRangeChange) {
          // Ensure logical chronological order when handing off the full range.
          const start = dayjs(d1).isBefore(d2) ? d1 : d2
          const end = dayjs(d1).isBefore(d2) ? d2 : d1
          onRangeChange(start, end)
        } else {
          // Manual selection without a range handler: preserve click order.
          onStartDateChange?.(d1)
          onEndDateChange?.(d2)
        }
      } else if (!onRangeChange) {
        onStartDateChange?.(s ? parseYmd(s) : null)
        onEndDateChange?.(e ? parseYmd(e) : null)
      }
    })
  }

  const handleDateRangeChange = (value: [string | null, string | null]) => {
    setInternalValue(value)
    propagate(value)
  }

  const activePresetIndex = resolvedPresets.findIndex(
    p => p.value[0] === internalValue[0] && p.value[1] === internalValue[1]
  )

  const handlePresetClick = (preset: RangePreset) => {
    setInternalValue(preset.value)
    setDisplayedDate(preset.value[0])
    propagate(preset.value)
  }

  return (
    <Group align="flex-start" gap="md" mb={mb} wrap="nowrap">
      <Stack gap={4} pr="sm" style={{ borderInlineEnd: '1px solid var(--mantine-color-default-border)' }}>
        {resolvedPresets.map((preset, index) => (
          <UnstyledButton
            key={preset.label}
            className="date-range-preset-button"
            data-active={index === activePresetIndex || undefined}
            onMouseDown={e => e.preventDefault()}
            onClick={() => handlePresetClick(preset)}
          >
            {preset.label}
          </UnstyledButton>
        ))}
      </Stack>
      <DatePicker
        headerControlsOrder={['level', 'previous', 'next']}
        type="range"
        value={internalValue}
        onChange={handleDateRangeChange}
        date={displayedDate}
        onDateChange={setDisplayedDate}
        size="md"
        allowSingleDateInRange
        styles={{
          calendarHeaderLevel: {
            justifyContent: 'flex-start',
            paddingInlineStart: 8,
          },
        }}
      />
    </Group>
  )
}
